import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Coins, Loader2, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CashDrawerPanel() {
  const qc = useQueryClient();
  const [date, setDate] = useState(iso(new Date()));
  const [form, setForm] = useState({ opening_float: "", cash_paid_out: "", counted_cash: "", note: "" });
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["mgr-cash-drawer", date],
    queryFn: () => api.get(`/manager/cash-drawer?date=${date}`).then((r) => r.data),
  });

  // Hydrate the form from an existing close for the selected date.
  useEffect(() => {
    const c = data?.current;
    if (c) setForm({
      opening_float: String(c.opening_float ?? ""), cash_paid_out: String(c.cash_paid_out ?? ""),
      counted_cash: String(c.counted_cash ?? ""), note: c.note || "",
    });
    else setForm({ opening_float: "", cash_paid_out: "", counted_cash: "", note: "" });
  }, [data?.current, date]);

  const cashSales = Number(data?.cash_sales || 0);
  const opening = parseFloat(form.opening_float) || 0;
  const paidOut = parseFloat(form.cash_paid_out) || 0;
  const counted = parseFloat(form.counted_cash) || 0;
  const expected = Math.round((opening + cashSales - paidOut) * 100) / 100;
  const variance = Math.round((counted - expected) * 100) / 100;
  const hasCounted = form.counted_cash !== "";

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/manager/cash-drawer/close", {
        date, opening_float: opening, cash_paid_out: paidOut, counted_cash: counted, note: form.note,
      });
      toast.success("Cash drawer close saved");
      qc.invalidateQueries({ queryKey: ["mgr-cash-drawer"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const varColor = variance === 0 ? "#5C8A72" : Math.abs(variance) < 0.01 ? "#5C8A72" : "#B45151";

  return (
    <div data-testid="mgr-cash-drawer-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Coins size={22} className="text-[#144F75]" /> Cash Drawer Close</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Count the drawer at end of day and reconcile against {data?.center_name || "your center"}'s POS cash tenders. Any variance is flagged.</p>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cash-drawer-date" className="eyw-input !py-1.5 text-sm" />
      </div>

      {isLoading ? (
        <div className="p-10 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>
      ) : data?.no_center ? (
        <div className="eyw-card p-8 text-center text-[#4A5D6B]">No center assigned yet.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="eyw-card p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Opening float</label>
                <input type="number" step="0.01" value={form.opening_float} onChange={(e) => set("opening_float", e.target.value)} placeholder="0.00" data-testid="cash-opening-float" className="eyw-input w-full" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1A242B] mb-1 block">Cash paid out</label>
                <input type="number" step="0.01" value={form.cash_paid_out} onChange={(e) => set("cash_paid_out", e.target.value)} placeholder="0.00" data-testid="cash-paid-out" className="eyw-input w-full" />
              </div>
            </div>
            <div className="flex items-center justify-between text-sm border-y border-[#F1EEE8] py-3" data-testid="cash-sales-row">
              <span className="text-[#4A5D6B]">POS cash sales (auto)</span>
              <span className="font-semibold text-[#1A242B]">{money(cashSales)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#4A5D6B]">Expected in drawer</span>
              <span className="font-semibold text-[#144F75]" data-testid="cash-expected">{money(expected)}</span>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Counted cash</label>
              <input type="number" step="0.01" value={form.counted_cash} onChange={(e) => set("counted_cash", e.target.value)} placeholder="0.00" data-testid="cash-counted" className="eyw-input w-full" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Note</label>
              <textarea rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="cash-note" className="eyw-input w-full resize-none" placeholder="Anything worth recording about today's drawer…" />
            </div>
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} data-testid="cash-drawer-save" className="eyw-btn !py-2.5 text-sm disabled:opacity-60">
                {saving && <Loader2 className="animate-spin" size={16} />} <Save size={15} /> {data?.current ? "Update close" : "Save close"}
              </button>
            </div>
          </div>

          <div className="space-y-5">
            <div className="eyw-card p-6" data-testid="cash-variance-card">
              <div className="flex items-center gap-2 text-xs text-[#8FA89B]">
                {hasCounted && Math.abs(variance) < 0.01 ? <CheckCircle2 size={15} className="text-[#5C8A72]" /> : <AlertTriangle size={15} style={{ color: varColor }} />} Variance (counted − expected)
              </div>
              <div className="text-4xl font-serif mt-2" style={{ color: hasCounted ? varColor : "#8FA89B" }} data-testid="cash-variance">
                {hasCounted ? `${variance > 0 ? "+" : ""}${money(variance)}` : "—"}
              </div>
              <p className="text-sm mt-2" style={{ color: varColor }}>
                {!hasCounted ? "Enter the counted cash to see the variance." : Math.abs(variance) < 0.01 ? "Balanced — the drawer matches expected cash." : variance > 0 ? "Over — more cash than expected." : "Short — less cash than expected."}
              </p>
            </div>

            <div className="eyw-card p-5" data-testid="cash-history">
              <h3 className="font-serif text-lg text-[#1A242B] mb-3">Recent closes</h3>
              <div className="space-y-2">
                {(data?.history || []).map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-sm border-b border-[#F1EEE8] pb-1.5" data-testid={`cash-history-${h.date}`}>
                    <span className="text-[#4A5D6B]">{fmtDate(h.date)}</span>
                    <span className="text-[#8FA89B]">counted {money(h.counted_cash)}</span>
                    <span className="font-semibold" style={{ color: Math.abs(h.variance) < 0.01 ? "#5C8A72" : "#B45151" }}>
                      {h.variance > 0 ? "+" : ""}{money(h.variance)}
                    </span>
                  </div>
                ))}
                {(data?.history || []).length === 0 && <p className="text-sm text-[#8FA89B]">No drawer closes recorded yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
